import { useBrokerAccountPositions } from '@/cip/api/position';
import { useAssetsByIds, useSRICategories } from '@/hooks/market-data-queries';
import { useSampledUniverse } from 'views/portfolio-engine/universe-queries';
import { useGetPortfolioTradePreferences } from 'views/portfolio/account-group-queries';

import { buildPersonalizedIndexingRows, PersonalizedIndexingRow } from './policy-personalized-indexing';
import { PolicyRetryQuery } from './PolicyQueryError';
import { getPositionsStatus, PositionsStatus } from './PositionAmount';
import { usePolicyScope } from './usePolicyScope';

type PersonalizedIndexingState =
    | { status: 'loading' | 'error'; retryQueries: PolicyRetryQuery[] }
    | {
          status: 'ready';
          rows: PersonalizedIndexingRow[];
          positionsStatus: PositionsStatus;
          retryQueries: PolicyRetryQuery[];
      };

export const usePolicyPersonalizedIndexing = (
    accountGUID: string,
    accountGroupGUID: string,
): PersonalizedIndexingState => {
    const scope = usePolicyScope(accountGUID, accountGroupGUID);
    const preferencesQuery = useGetPortfolioTradePreferences(accountGroupGUID, { throwOnError: false });
    const preferences = preferencesQuery.data;
    const brokerIds = scope.accounts.flatMap((item) => (item.brokerAccountId ? [item.brokerAccountId] : []));
    const positions = useBrokerAccountPositions(brokerIds, { throwOnError: false });
    const sri = useSRICategories({ refetchOnMount: false, refetchOnWindowFocus: false, throwOnError: false });
    const hasSriScreens = !!preferences?.restrictedCategoryIds.length;
    const restrictedIds = [...new Set(preferences?.restrictions.map(({ shareId }) => shareId) ?? [])];
    const assets = useAssetsByIds(restrictedIds, { throwOnError: false });
    const universe = useSampledUniverse(restrictedIds.length > 0);
    const retryQueries = [...scope.retryQueries, preferencesQuery, positions, sri, assets, universe];

    if (scope.status === 'error' || preferencesQuery.isLoadingError || (hasSriScreens && sri.isLoadingError)) {
        return { status: 'error', retryQueries };
    }
    if (scope.status === 'loading' || preferencesQuery.isPending || (hasSriScreens && sri.isPending)) {
        return { status: 'loading', retryQueries };
    }
    if (!preferences || (hasSriScreens && !sri.data)) return { status: 'error', retryQueries };

    return {
        status: 'ready',
        retryQueries,
        positionsStatus: getPositionsStatus(positions, brokerIds.length > 0),
        rows: buildPersonalizedIndexingRows(
            sri.data ?? [],
            preferences,
            assets.data ?? [],
            brokerIds.length ? positions.data : [],
            universe.data,
        ),
    };
};
